import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Copy, Send, ExternalLink } from "lucide-react";

// "Challenge your neighbours" share card (Phase 12.1). Share targets:
// (a) post to a collective noticeboard the user belongs to (member-only,
//     server enforces the once-per-day-per-collective anti-spam rule),
// (b) copy text + link fallback,
// (c) the OG PNG rendered live by /api/og/area/{area} (same aggregates).
export const AreaChallengeDialog = ({ area, message, open, onOpenChange }) => {
  const [collectives, setCollectives] = useState(null); // null = loading
  const [target, setTarget] = useState("");
  const [posting, setPosting] = useState(false);
  const cardUrl = `${process.env.REACT_APP_BACKEND_URL}/api/og/area/${encodeURIComponent(area || "")}`;

  useEffect(() => {
    if (!open) return;
    api.myCollectives()
      .then((d) => {
        const list = d?.collectives || [];
        setCollectives(list);
        if (list.length) setTarget(list[0].id);
      })
      .catch(() => setCollectives([]));
  }, [open]);

  const post = async () => {
    if (!target) return;
    setPosting(true);
    try {
      await api.postAreaChallenge(area, target);
      const c = collectives.find((x) => x.id === target);
      toast.success(`Challenge posted to ${c?.name || "your collective"}'s noticeboard`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post the challenge");
    }
    setPosting(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${message} Join in: ${window.location.origin}/leaderboard?tab=areas`);
      toast.success("Challenge copied — paste it anywhere");
    } catch {
      toast.error("Couldn't access the clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="area-challenge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-forest flex items-center gap-2">
            <Flame className="w-5 h-5 text-terracotta" /> Challenge your neighbours
          </DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <img
          src={cardUrl}
          alt={`${area} neighbourhood challenge card`}
          data-testid="area-challenge-card-img"
          className="w-full rounded-xl border border-forest/10"
        />

        {collectives === null && <div className="text-sm text-neutral-500">Loading your collectives…</div>}
        {collectives?.length > 0 && (
          <div className="flex gap-2">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="area-challenge-collective-select" className="flex-1 rounded-full h-10 text-sm">
                <SelectValue placeholder="Pick a collective" />
              </SelectTrigger>
              <SelectContent>
                {collectives.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="area-challenge-post-btn" onClick={post} disabled={posting || !target} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-5">
              <Send className="w-4 h-4 mr-1.5" /> Post
            </Button>
          </div>
        )}
        {collectives?.length === 0 && (
          <p className="text-sm text-neutral-500">
            You're not in a collective yet — <Link to="/collectives" className="text-forest underline underline-offset-2">find one near you</Link> to rally, or just copy the challenge below.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button data-testid="area-challenge-copy-btn" variant="outline" onClick={copy} className="rounded-full h-10 px-5 border-forest/20 text-forest">
            <Copy className="w-4 h-4 mr-1.5" /> Copy text
          </Button>
          <a href={cardUrl} target="_blank" rel="noreferrer" data-testid="area-challenge-image-link">
            <Button variant="outline" className="rounded-full h-10 px-5 border-forest/20 text-forest">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Open image
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
